import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'

import * as chatApi from '@/services/api/endpoints/chat'
import { ChatConversation, ChatMessage, SendMessagePayload, StartConversationPayload } from '@/services/api/endpoints/chat'
import { ApiError } from '@/services/api/types'

interface ChatState {
  activeConversationId: string | null
  conversations: ChatConversation[]
  conversationsLoading: boolean
  error: string | null
  messages: ChatMessage[]
  messagesLoading: boolean
  sendingMessage: boolean
  typingUserIds: string[]
}

const initialState: ChatState = {
  activeConversationId: null,
  conversations: [],
  conversationsLoading: false,
  error: null,
  messages: [],
  messagesLoading: false,
  sendingMessage: false,
  typingUserIds: [],
}

const errorMessage = (error: unknown, fallback: string): string => (error instanceof ApiError ? error.message : fallback)

export const fetchConversations = createAsyncThunk('chat/fetchConversations', async (_, { rejectWithValue }) => {
  try {
    return await chatApi.listConversations()
  } catch (error) {
    return rejectWithValue(errorMessage(error, 'Failed to load conversations'))
  }
})

export const startOrGetConversation = createAsyncThunk(
  'chat/startOrGetConversation',
  async (payload: StartConversationPayload, { rejectWithValue }) => {
    try {
      return await chatApi.startConversation(payload)
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to start conversation'))
    }
  },
)

export const fetchMessages = createAsyncThunk(
  'chat/fetchMessages',
  async ({ conversationId, limit, page }: { conversationId: string; limit?: number; page?: number }, { rejectWithValue }) => {
    try {
      const result = await chatApi.listMessages(conversationId, { limit, page })
      return result.data
    } catch (error) {
      return rejectWithValue(errorMessage(error, 'Failed to load messages'))
    }
  },
)

export const sendMessage = createAsyncThunk(
  'chat/sendMessage',
  async ({ conversationId, payload }: { conversationId: string; payload: SendMessagePayload }, { rejectWithValue }) => {
    try {
      return await chatApi.sendMessageRest(conversationId, payload)
    } catch (error) {
      const message = errorMessage(error, 'Failed to send message')
      toast.error(message)
      return rejectWithValue(message)
    }
  },
)

const chatSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversations.pending, (state) => {
        state.conversationsLoading = true
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.conversations = action.payload
        state.conversationsLoading = false
      })
      .addCase(fetchConversations.rejected, (state) => {
        state.conversationsLoading = false
      })
      .addCase(startOrGetConversation.fulfilled, (state, action) => {
        state.activeConversationId = action.payload.id
        if (!state.conversations.some((c) => c.id === action.payload.id)) {
          state.conversations.unshift(action.payload)
        }
      })
      .addCase(fetchMessages.pending, (state) => {
        state.messagesLoading = true
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.messages = action.payload
        state.messagesLoading = false
      })
      .addCase(fetchMessages.rejected, (state) => {
        state.messagesLoading = false
      })
      .addCase(sendMessage.pending, (state) => {
        state.sendingMessage = true
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sendingMessage = false
        state.messages.push(action.payload)
      })
      .addCase(sendMessage.rejected, (state) => {
        state.sendingMessage = false
      })
  },
  initialState,
  name: 'chat',
  reducers: {
    clearActiveConversation: (state) => {
      state.activeConversationId = null
      state.messages = []
      state.typingUserIds = []
    },
    /** Driven by the chat socket's `message_deleted` event - matches the
     * REST delete's own behavior of no longer returning deleted messages. */
    messageDeleted: (state, action: { payload: { conversationId: string; messageId: string } }) => {
      const { conversationId, messageId } = action.payload
      state.messages = state.messages.filter((m) => m.id !== messageId)
      const conversation = state.conversations.find((c) => c.id === conversationId)
      if (conversation?.lastMessage?.id === messageId) conversation.lastMessage = null
    },
    /** Driven by the chat socket's `message_edited` event. */
    messageEdited: (state, action: { payload: { message: ChatMessage } }) => {
      const { message } = action.payload
      const index = state.messages.findIndex((m) => m.id === message.id)
      if (index !== -1) state.messages[index] = message
      const conversation = state.conversations.find((c) => c.id === message.conversationId)
      if (conversation?.lastMessage?.id === message.id) conversation.lastMessage = message
    },
    /** Driven by the chat socket's `new_message` event - covers both messages
     * this client sent (the gateway broadcasts to the whole room, sender
     * included) and messages received from others. */
    messageReceived: (state, action: { payload: { conversationId: string; currentUserId?: string; message: ChatMessage } }) => {
      const { conversationId, currentUserId, message } = action.payload
      const isActive = conversationId === state.activeConversationId
      if (isActive && !state.messages.some((m) => m.id === message.id)) {
        state.messages.push(message)
      }

      const conversation = state.conversations.find((c) => c.id === conversationId)
      if (conversation) {
        conversation.lastMessage = message
        conversation.updatedAt = message.createdAt
        const isMine = message.sender?.id === currentUserId
        if (!isActive && !isMine) {
          conversation.unreadCount = (conversation.unreadCount ?? 0) + 1
        }
      }
    },
    setActiveConversation: (state, action: { payload: string }) => {
      state.activeConversationId = action.payload
      state.messages = []
      state.typingUserIds = []
    },
    setUserTyping: (state, action: { payload: { isTyping: boolean; userId: string } }) => {
      const { isTyping, userId } = action.payload
      state.typingUserIds = isTyping
        ? Array.from(new Set([...state.typingUserIds, userId]))
        : state.typingUserIds.filter((id) => id !== userId)
    },
  },
})

export const { clearActiveConversation, setActiveConversation, messageReceived, messageEdited, messageDeleted, setUserTyping } = chatSlice.actions

interface RootStateSlice {
  chat: ChatState
}

export const selectConversations = (state: RootStateSlice): ChatConversation[] => state.chat.conversations
export const selectConversationsLoading = (state: RootStateSlice): boolean => state.chat.conversationsLoading
export const selectActiveConversationId = (state: RootStateSlice): string | null => state.chat.activeConversationId
export const selectMessages = (state: RootStateSlice): ChatMessage[] => state.chat.messages
export const selectMessagesLoading = (state: RootStateSlice): boolean => state.chat.messagesLoading
export const selectSendingMessage = (state: RootStateSlice): boolean => state.chat.sendingMessage
export const selectTypingUserIds = (state: RootStateSlice): string[] => state.chat.typingUserIds
export const selectTotalUnreadCount = (state: RootStateSlice): number =>
  state.chat.conversations.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0)

export default chatSlice.reducer
